// Route-level cache warming.
//
// THE ONE RULE: a loader never returns data, and no component reads
// `useLoaderData`.
//
// A loader that returns data creates a second, non-reactive copy of server
// state — one that a mutation cannot invalidate and a poll cannot refresh —
// which is exactly the duplication this whole layer exists to remove. Making
// `warm` return `Promise<void>` enforces that structurally rather than by
// convention: there is nothing to return.
//
// This is sound because `ensureQueryData` fills the same cache slot the
// component's `useQuery` reads. The resource is declared once, so the loader
// and the hook cannot disagree about the key — which is the single most likely
// way to write a loader that looks like it works and does nothing.
//
// Why this did not exist: 0 of 36 routes had a `loader`. The plumbing was all
// there — `__root.tsx` already builds the router with a typed context carrying
// `queryClient`, and `route-guards.ts` already awaits `fetchQuery` inside
// `beforeLoad` — but only auth ever used it, so every screen fetched on mount
// and rendered its empty state first.

import { isCancelledError, type FetchQueryOptions, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { resourceCoherencyFromMeta } from '@/api/resource';
import { registerRouteIntent } from '@/cache/data-revision-coordinator';

// A variadic warmer over heterogeneous resources. Each option keeps its own
// response type: `TData` is a tuple inferred element by element from the
// arguments, so no element is widened. (`FetchQueryOptions<unknown, …>` cannot
// stand in for them: `staleTime` takes the query as a parameter, so an option
// typed for one response is not assignable to one typed for `unknown`.)
type QueryOptionsFor<TData> = FetchQueryOptions<TData, Error, TData, QueryKey>;
type QueryOptionsList<TData extends readonly unknown[]> = {
  readonly [K in keyof TData]: QueryOptionsFor<TData[K]>;
};

/** What the revision check reads from an option, whatever its response type. */
interface OptionMeta {
  readonly meta?: FetchQueryOptions['meta'];
}

function needsRevisionCheck(options: readonly OptionMeta[]): boolean {
  return options.some((option) => {
    const coherency = resourceCoherencyFromMeta(option.meta);
    return coherency != null && 'domains' in coherency;
  });
}

async function checkRevisionsForIntent(options: readonly OptionMeta[]): Promise<void> {
  if (!needsRevisionCheck(options)) return;
  // A manifest failure is a background-refresh failure, not permission to
  // blank a destination whose last coherent result is still available. The
  // coordinator records it for the global status control; the route continues
  // through its ordinary data/error behavior.
  await registerRouteIntent().catch(() => undefined);
}

/**
 * Fill the cache before the route renders, and block on it.
 *
 * `revalidateIfStale` is what gives a returning user the right shape: paint
 * immediately from the warm in-memory slot, then refresh in the background.
 * Without it a stale-but-present slot would either block on a
 * refetch or never refresh at all, depending on the resource's freshness tier.
 */
export async function warm<const TData extends readonly unknown[]>(
  queryClient: QueryClient,
  ...options: QueryOptionsList<TData>
): Promise<void> {
  await checkRevisionsForIntent(options);
  await Promise.all(
    options.map(<T>(o: QueryOptionsFor<T>) =>
      surviveCancel(queryClient, o.queryKey, () => queryClient.ensureQueryData({ ...o, revalidateIfStale: true }))),
  );
}

/**
 * How many times a fetch is re-asked after it was cancelled underneath its caller. Each cancel
 * needs an observer to leave mid-flight; more than a couple in a row is a loop, and then the
 * caller sees the cancel like any other failure.
 */
const CANCELLED_REFETCHES = 2;

/**
 * Run `ask` (a `fetchQuery` / `ensureQueryData` call on `queryKey` that a route awaits) and ask
 * again when the fetch it joined was cancelled underneath it. The shell observes some of the
 * keys a route awaits (the session, and keys its loaders warm); when the last observer of an
 * in-flight query leaves (StrictMode's double-invoke in development, a real unmount in
 * production), TanStack cancels the fetch with `revert` and every promise awaiting it rejects
 * with a CancelledError, which the route then rendered as "Couldn't load this screen"
 * (src/api/warm-cancel.test.ts).
 *
 * A cancel that came with the query's removal is never re-asked: that is a purge meaning it
 * (`clearPrivateQueryCache` on a 401 or a logout), and asking again would revive the very
 * query it removed (Astra code review round 3, D3). Such a query is gone from the cache, or
 * replaced by a new one; one cancelled because its observers left is the same object, still there.
 */
export async function surviveCancel<T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  ask: () => Promise<T>,
): Promise<T> {
  const current = () => queryClient.getQueryCache().find({ queryKey, exact: true });
  for (let attempt = 0; ; attempt += 1) {
    // `ask` builds (or joins) the query synchronously; this is the one it waits on.
    const asked = ask();
    const query = current();
    try {
      return await asked;
    } catch (error) {
      // The same query object, still cached: its observers left. Removed, or removed and
      // recreated by another observer, is a purge (Astra code review round 4, E2).
      const sameQuery = query !== undefined && current() === query;
      if (!isCancelledError(error) || !sameQuery || attempt >= CANCELLED_REFETCHES) throw error;
    }
  }
}

/**
 * Fill the cache without blocking the route.
 *
 * For anything below the fold, or a panel whose absence does not stop the page
 * being useful. Blocking a route on every query it will eventually make is how
 * a prefetch layer turns into a slower page than no prefetch layer.
 */
export function warmBackground<const TData extends readonly unknown[]>(
  queryClient: QueryClient,
  ...options: QueryOptionsList<TData>
): void {
  void checkRevisionsForIntent(options).then(() => {
    for (const o of options) void queryClient.prefetchQuery(o);
  });
}
